import React from 'react';
import { Award, Globe, Users, TrendingUp, CheckCircle, Target } from 'lucide-react';
import { mockData } from '../mock';

export const About = () => {
  return (
    <section id="about" className="about-section">
      <div className="container">
        <div className="about-content">
          {/* About Text */}
          <div className="about-text">
            <h2 className="heading-1 mb-4">About GreenZaar</h2>
            <p className="body-large mb-6">
              Established as a leading supplier of premium Guar Gum products, GreenZaar has been at the forefront of delivering exceptional quality and reliability to industries worldwide. With operations spanning across USA and India, we bridge the gap between traditional sourcing and modern industrial requirements.
            </p>
            <p className="body-medium mb-8">
              Our commitment to excellence extends beyond just supplying guar gum – we partner with our clients to understand their specific needs and provide tailored solutions that enhance their product performance and operational efficiency.
            </p>
            
            {/* Key Features */}
            <div className="about-features">
              <div className="feature-item">
                <CheckCircle className="feature-icon" size={20} />
                <span>15+ Years of Industry Experience</span>
              </div>
              <div className="feature-item">
                <CheckCircle className="feature-icon" size={20} />
                <span>ISO 9001:2015 & FDA Certified Facilities</span>
              </div>
              <div className="feature-item">
                <CheckCircle className="feature-icon" size={20} />
                <span>Global Supply Chain Network</span>
              </div>
              <div className="feature-item">
                <CheckCircle className="feature-icon" size={20} />
                <span>Custom Specification Development</span>
              </div>
            </div>
          </div>
          
          {/* About Info Card */}
          <div className="about-info-card">
            <div className="info-card-content">
              <h3 className="heading-3 mb-4">Why Choose GreenZaar?</h3>
              <div className="info-points">
                <div className="info-point">
                  <CheckCircle className="info-icon" size={20} />
                  <span>Consistent quality across all product grades</span>
                </div>
                <div className="info-point">
                  <CheckCircle className="info-icon" size={20} />
                  <span>Advanced testing and quality control</span>
                </div>
                <div className="info-point">
                  <CheckCircle className="info-icon" size={20} />
                  <span>Reliable supply chain management</span>
                </div>
                <div className="info-point">
                  <CheckCircle className="info-icon" size={20} />
                  <span>Technical support and customization</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Stats Section */}
        <div className="stats-section">
          <div className="stat-item">
            <Award className="stat-icon" size={32} />
            <div className="stat-number">15+</div>
            <div className="stat-label">Years Experience</div>
          </div>
          <div className="stat-item">
            <Globe className="stat-icon" size={32} />
            <div className="stat-number">50+</div>
            <div className="stat-label">Countries Served</div>
          </div>
          <div className="stat-item">
            <Users className="stat-icon" size={32} />
            <div className="stat-number">200+</div>
            <div className="stat-label">Satisfied Clients</div>
          </div>
          <div className="stat-item">
            <TrendingUp className="stat-icon" size={32} />
            <div className="stat-number">99.8%</div>
            <div className="stat-label">Quality Assurance</div>
          </div>
        </div>
        
        {/* Mission & Vision */}
        <div className="mission-vision">
          <div className="mission-card network-card">
            <Target className="mission-icon" size={32} />
            <h3 className="heading-3 mb-4">Our Mission</h3>
            <p className="body-medium">
              To provide the highest quality guar gum products while maintaining sustainable practices and fostering long-term partnerships with our clients across diverse industries worldwide.
            </p>
          </div>
          <div className="vision-card network-card">
            <Award className="vision-icon" size={32} />
            <h3 className="heading-3 mb-4">Our Vision</h3>
            <p className="body-medium">
              To be the globally recognized leader in guar gum supply, known for innovation, reliability, and commitment to excellence in every product we deliver.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Styles
const styles = `
.about-section {
  background: white;
  padding: var(--spacing-giant) 0;
}

.about-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-giant);
  margin-bottom: var(--spacing-giant);
  align-items: center;
}

.about-text {
  padding-right: var(--spacing-medium);
}

.about-features {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  color: var(--text-secondary);
}

.feature-icon {
  color: var(--brand-dark);
  flex-shrink: 0;
}

.about-info-card {
  background: var(--bg-subtle);
  border-radius: 20px;
  padding: var(--spacing-xl);
  height: 400px;
  display: flex;
  align-items: center;
  border: 1px solid var(--border-light);
}

.info-card-content {
  width: 100%;
}

.info-points {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-point {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  color: var(--text-secondary);
}

.info-icon {
  color: var(--brand-dark);
  flex-shrink: 0;
}

.stats-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-large);
  margin-bottom: var(--spacing-giant);
  padding: var(--spacing-xl) 0;
  background: var(--bg-subtle);
  border-radius: 32px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: var(--spacing-medium);
}

.stat-icon {
  color: var(--brand-dark);
  margin-bottom: 16px;
}

.stat-number {
  font-size: 36px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 14px;
  color: var(--text-light);
  font-weight: 500;
}

.mission-vision {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-large);
}

.mission-card,
.vision-card {
  text-align: center;
  padding: var(--spacing-xl);
}

.mission-icon,
.vision-icon {
  color: var(--brand-dark);
  background: var(--brand-primary);
  padding: 12px;
  border-radius: 50%;
  margin: 0 auto 20px;
  width: 56px;
  height: 56px;
}

.text-white {
  color: white !important;
}

@media (max-width: 1024px) {
  .about-content {
    grid-template-columns: 1fr;
    gap: var(--spacing-xl);
  }
  
  .about-text {
    padding-right: 0;
  }
  
  .stats-section {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--spacing-medium);
  }
}

@media (max-width: 781px) {
  .about-content {
    gap: var(--spacing-large);
  }
  
  .about-image {
    height: 300px;
  }
  
  .stats-section {
    grid-template-columns: 1fr;
    gap: var(--spacing-medium);
  }
  
  .mission-vision {
    grid-template-columns: 1fr;
    gap: var(--spacing-medium);
  }
  
  .stat-number {
    font-size: 28px;
  }
}
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = styles;
  document.head.appendChild(styleSheet);
}